/**
 * Copyright (c) 2025 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { css, StyleSheet } from 'aphrodite';
import { MaterialIcon } from '@blend/enterprise-components';
import { VerticalSectionCard } from '../VerticalSectionCard/VerticalSectionCard';
import { BlendBridge } from 'blb-types/blendBridge';
import { useState, useContext } from 'react';
import { completeTask } from 'blb-api/tasks';
import { errorMessage } from 'blb-utils/errors';
import { get } from 'lodash';
import { MeContext } from 'blb-contexts/Me';
import { TaskModal } from '../TaskModal';
import { Form } from 'react-final-form';
import { Text } from '@blend/enterprise-components';

const styles = StyleSheet.create({
  confirmationRow: {
    display: 'flex',
    alignItems: 'center',
    background: '#F7FDFC',
    borderRadius: 3,
    padding: '16px 32px',
    flexWrap: 'nowrap',
    gap: 12,
    width: '100%',
  },
  checkIcon: {
    color: '#00B294',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  confirmationText: {
    color: '#475173',
    fontWeight: 400,
    lineHeight: '28px',
    whiteSpace: 'nowrap',
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '24px 32px',
  },
  modalText: {
    margin: '0 auto',
    textAlign: 'left',
  },
});

export namespace CancelClosingDocumentsTask {
  export interface Props extends Omit<BlendBridge.API.CancelClosingDocumentsTask, 'type'> {
    taskId: string;
    onSuccess: () => Promise<any>;
  }
}
export const CancelClosingDocumentsTask: React.FC<CancelClosingDocumentsTask.Props> = ({
  disabledReason,
  isDisabled,
  taskId,
  onSuccess,
}) => {
  const me = useContext(MeContext);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const handleFormSubmit = async () => {
    if (taskId) {
      try {
        setLoading(true);
        await completeTask(taskId);
        await onSuccess();
      } catch (err) {
        setError(get(err, 'response.data.error.message', errorMessage('SENDING_FORM', me?.principalType)));
        setLoading(false);
        setIsModalOpen(false);
      }
    }
    setIsModalOpen(false);
  };

  const handleClick = () => {
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setError(undefined);
    setIsModalOpen(false);
  };

  return (
    <Form onSubmit={handleFormSubmit}>
      {({ handleSubmit }) => (
        <>
          <VerticalSectionCard
            buttonLabel="Cancel release of closing documents"
            description={
              <div className={css(styles.confirmationRow)}>
                <span className={css(styles.checkIcon)}>
                  <MaterialIcon size={32} type="check_circle" />
                </span>
                <span className={css(styles.confirmationText)}>
                  E-sign documents have been sent to the borrower for signing
                </span>
              </div>
            }
            disabled={isDisabled || isLoading}
            disabledReason={disabledReason}
            onButtonClick={handleClick}
            title="Release documents for signing"
          />
          <TaskModal
            alternateButtonLabel="Cancel"
            divideFooter={true}
            error={error}
            handleAlternateClick={handleClose}
            handleClose={handleClose}
            handleSubmit={handleSubmit}
            isLoading={isLoading}
            isOpen={isModalOpen}
            submitButtonLabel="Cancel release of closing documents"
            taskId={taskId}
            title="Cancel release of closing documents"
          >
            <div className={css(styles.modalText)}>
              <Text color="secondary" fontSize="md">
                This action will cancel the closing package and dissallow the borrower from signing. If the closing
                documents need to be reissued later, the closing must be cancelled and resent.
              </Text>
            </div>
          </TaskModal>
        </>
      )}
    </Form>
  );
};
